<template>
    <div class="flex items-baseline gap-1.5">
        <span
            v-for="segment in segments"
            :key="segment.key"
            class="flex items-baseline gap-1"
        >
            <span :class="valueClass">{{ segment.value }}</span>
            <span :class="unitClass">{{ segment.unit }}</span>
        </span>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

interface Props {
    targetDate: Date | string
    valueClass?: string
    unitClass?: string
}

const props = withDefaults(defineProps<Props>(), {
    valueClass: undefined,
    unitClass: undefined
})

const { t } = useI18n()
const remainingTime = ref({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
})
const intervalId = ref<ReturnType<typeof setInterval> | null>(null)

const pad = (value: number) => value.toString().padStart(2, '0')

const segments = computed(() => {
    const { days, hours, minutes } = remainingTime.value

    return [
        { key: 'days', value: pad(days), unit: t('misc.countdown.days') },
        { key: 'hours', value: pad(hours), unit: t('misc.countdown.hoursShort') },
        { key: 'minutes', value: pad(minutes), unit: t('misc.countdown.minutesShort') }
    ]
})

const updateCountdown = () => {
    const now = new Date().getTime()

    try {
        // Convert string to Date object if needed
        const targetDate = typeof props.targetDate === 'string'
            ? new Date(props.targetDate)
            : props.targetDate

        // Check if date is valid
        if (isNaN(targetDate.getTime())) {
            console.error('Invalid date provided to countdown component:', props.targetDate)
            remainingTime.value = { days: 0, hours: 0, minutes: 0, seconds: 0 }
            return
        }

        const targetTime = targetDate.getTime()
        const difference = Math.max(0, targetTime - now)

        if (difference <= 0) {
            remainingTime.value = { days: 0, hours: 0, minutes: 0, seconds: 0 }
            if (intervalId.value !== null) {
                clearInterval(intervalId.value)
                intervalId.value = null
            }
            return
        }

        // Calculate time units
        const days = Math.floor(difference / (1000 * 60 * 60 * 24))
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((difference % (1000 * 60)) / 1000)

        remainingTime.value = { days, hours, minutes, seconds }
    } catch (error) {
        console.error('Error in countdown calculation:', error)
        remainingTime.value = { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }
}

onMounted(() => {
    // Initial update
    updateCountdown()

    // Set up interval to update every second
    intervalId.value = setInterval(updateCountdown, 1000)
})

onBeforeUnmount(() => {
    // Clean up interval on component unmount
    if (intervalId.value !== null) {
        clearInterval(intervalId.value)
        intervalId.value = null
    }
})
</script>
